const HEX_COLOR_REGEX = /^#[0-9A-Fa-f]{6}$/;

export const TAG_COLOR_HEX_BY_NAME = {
  BLUE: "#3B82F6",
  RED: "#EF4444",
  PURPLE: "#8B5CF6",
} as const;

export type InboxTagColorName = keyof typeof TAG_COLOR_HEX_BY_NAME;

export const SUPPORTED_INBOX_TAG_COLORS = [
  { name: "BLUE", label: "Blue", hex: TAG_COLOR_HEX_BY_NAME.BLUE },
  { name: "RED", label: "Red", hex: TAG_COLOR_HEX_BY_NAME.RED },
  { name: "PURPLE", label: "Purple", hex: TAG_COLOR_HEX_BY_NAME.PURPLE },
] as const;
export const SUPPORTED_INBOX_TAG_HEX_COLORS = SUPPORTED_INBOX_TAG_COLORS.map(
  (color) => color.hex
);

const FALLBACK_TAG_COLOR = TAG_COLOR_HEX_BY_NAME.BLUE;
const TAG_COLOR_NAME_SET = new Set(Object.keys(TAG_COLOR_HEX_BY_NAME));
// Store supported hexes in uppercase so lowercase user input can be normalized
// before the membership check.
const TAG_COLOR_HEX_SET = new Set<string>(SUPPORTED_INBOX_TAG_HEX_COLORS);

function isInboxTagColorName(color: string): color is InboxTagColorName {
  return TAG_COLOR_NAME_SET.has(color);
}

function hexToRgb(hex: string) {
  const safe = safeTagColor(hex);
  return {
    r: Number.parseInt(safe.slice(1, 3), 16),
    g: Number.parseInt(safe.slice(3, 5), 16),
    b: Number.parseInt(safe.slice(5, 7), 16),
  };
}

/**
 * Validates that a string is a valid 6-digit hex color (e.g. "#3B82F6").
 */
export function isValidHexColor(color: string): boolean {
  return HEX_COLOR_REGEX.test(color);
}

export function isSupportedInboxTagHexColor(color: string): boolean {
  return isValidHexColor(color) && TAG_COLOR_HEX_SET.has(color.toUpperCase());
}

/**
 * Returns a safe display hex color for either a hex string or API enum value.
 * Use this before rendering user-controlled colors in styles.
 */
export function safeTagColor(color: string): string {
  if (isInboxTagColorName(color)) {
    return TAG_COLOR_HEX_BY_NAME[color];
  }
  return isValidHexColor(color) ? color : FALLBACK_TAG_COLOR;
}

/**
 * Converts a UI color value into a backend-safe color.
 * The tags API accepts legacy enum colors and modern #RRGGBB values.
 */
export function serializeTagColor(color: string): string {
  if (isInboxTagColorName(color)) {
    return color;
  }

  if (isValidHexColor(color)) {
    return color;
  }

  return FALLBACK_TAG_COLOR;
}

/**
 * Converts a validated hex color to an rgba() string with the given opacity.
 * Always validates the hex first to prevent XSS via style injection.
 */
export function hexToRgba(hex: string, opacity: number): string {
  const { r, g, b } = hexToRgb(hex);
  return `rgba(${r}, ${g}, ${b}, ${opacity})`;
}

/**
 * Foreground color for tag pills. Tag badges paint the tag color at ~12.5%
 * opacity over white, so the background is always light — neutral-900 gives
 * a safe AA-compliant foreground regardless of which supported hex is used.
 */
export const TAG_PILL_FOREGROUND = "#111827";
